/**
 * Automatic replies state shared by the mail view banner and the settings page: the layout loads it once and the
 * settings page publishes what it saved.
 */
import { useEffect, useState } from 'react';
import { api, type AutomaticReplies } from './api';

type Listener = (value: AutomaticReplies | undefined) => void;
const listeners = new Set<Listener>();
let latest: AutomaticReplies | undefined;

/** Makes `value` the current state for every subscriber (banner, settings page). */
export function publishAutomaticReplies(value: AutomaticReplies | undefined): void {
  latest = value;
  listeners.forEach((listener) => listener(value));
}

/** Current state; loads it once per `enabled` session (a failed load shows no banner). */
export function useAutomaticReplies(enabled: boolean): AutomaticReplies | undefined {
  const [value, setValue] = useState(latest);
  useEffect(() => {
    listeners.add(setValue);
    return () => void listeners.delete(setValue);
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    api.myAutomaticReplies().then(
      (loaded) => active && publishAutomaticReplies(loaded),
      () => undefined,
    );
    return () => { active = false; };
  }, [enabled]);
  return enabled ? value : undefined;
}
